import { FC, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import queryString from 'query-string';
import Axios, { CancelTokenSource } from 'axios';
import { produce } from 'immer';

import { getUserFullName } from '~src/interfaces/user.interface';
import logger from '~src/services/logger/LlsLogger';
import Pagination from '~src/shared/lists/pagination/Pagination';
import ItemsAmount from '~src/shared/lists/itemsAmount/ItemsAmount';
import { StoreI } from '~src/rootReducer';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import { routes } from '~src/routingConfig';
import { MainContainerAnimation } from '~src/styles/animations';
import { PaginationI } from '~src/interfaces/pagination.interface';
import {
  logError,
  fetchPreviewList,
  postToggleIncludeUser,
  cancelImport,
  postChangeImportState,
  fetchImportStatus,
  UserImportPreviewResp,
} from '~src/services/fetch/import.service';
import { ImportPreviewItemI } from '~src/services/fetch/import.service';
import { AttributeI } from '~src/interfaces/attributes.types';
import { translateContent } from '~src/services/translationServices';
import ProgressBar from '~src/shared/ProgressBar';
import PageHeader from '~src/modules/companySettings/importUsers/PageHeader';

import UserPreviewItem, { UserPreviewAttrI, UserPreviewData, UserPreviewStatus } from './UserPreviewItem';
import {
  InfoSortingBlock,
  ItemsListContainer,
  ImportBtnsBox,
  PageContentContainerS,
  ListContainer,
  ProgressBox,
  NoItemsText,
} from './usersPreviewList.sc';

const attributeNames: { [keys: string]: string } = {
  firstname: 'x.firstname',
  lastname: 'x.lastname',
  email: 'x.email',
  is_admin: 'x.company.import.is_admin_attribute',
  username: 'x.username',
};

interface PreviewItemsI {
  items: UserPreviewData[];
  pagination: PaginationI;
}

type SysAttrs = 'lls_content_language' | 'lls_system_language' | 'lls_timezone';

interface StateProps {
  sysAttrs: Record<SysAttrs, Record<string, string>>;
  customAttrs: { [key: string]: AttributeI };
  loadedLocalesAndTimezones: boolean;
}

type Props = StateProps & RouteComponentProps;

const UsersPreviewList: FC<Props> = props => {
  const [t] = useTranslation();
  const [initialPage, setInitialPage] = useState(1);
  const [startingImport, setStartingImport] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const importTimeout = useRef<number>();
  const importStatusToken = useRef<CancelTokenSource>();
  const sysAttrsOrUsersLoaded = useRef<boolean | UserImportPreviewResp>(false);
  const [importProgress, setImportProgress] = useState<number>();
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [previewItems, setPreviewItems] = useState<PreviewItemsI>();
  const attrNames = useMemo(() => {
    return Object.entries(attributeNames).reduce<{ [key: string]: string[] }>((acc, [key, val]) => {
      acc[key] = [val, t(val)];
      return acc;
    }, {});
  }, []);
  const { importId } = props.match.params as { importId: string };
  const isSaveDisabled = useMemo<boolean>(() => {
    if (previewItems && previewItems.items && previewItems.pagination.last_page === 1) {
      return !previewItems.items.some(item => item.isSelected);
    }
    return false;
  }, [previewItems && previewItems.items, previewItems && previewItems.pagination]);

  useEffect(() => {
    const searchStrObj: { page?: string } = queryString.parse(props.location.search);
    const initPage = Number(searchStrObj.page) || 1;
    setInitialPage(initPage);
    getUsers(initPage);
    return () => {
      if (importTimeout.current) {
        clearTimeout(importTimeout.current);
      }
      if (importStatusToken.current) {
        importStatusToken.current.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (props.loadedLocalesAndTimezones) {
      if (!sysAttrsOrUsersLoaded.current) {
        sysAttrsOrUsersLoaded.current = true;
      } else if (typeof sysAttrsOrUsersLoaded.current === 'object') {
        updatePreviewItems(sysAttrsOrUsersLoaded.current);
      }
    }
  }, [props.loadedLocalesAndTimezones]);

  const getUsers = async (page = 1) => {
    setLoadingPreview(true);
    try {
      const { data } = await fetchPreviewList({
        page,
        include: 'actions',
        'filter.preview': true,
        'filter.import': importId,
        sort: 'has_errors',
      });
      if (sysAttrsOrUsersLoaded.current) {
        updatePreviewItems(data);
      } else {
        sysAttrsOrUsersLoaded.current = data;
      }
    } catch (err) {
      logError('Error on getting preview import items', '' + err, 'UsersPreviewList.tsx');
    }
    setLoadingPreview(false);
  };

  const updatePreviewItems = (data: UserImportPreviewResp) => {
    const previewUsers: UserPreviewData[] = data.items.map(user => {
      let status: UserPreviewStatus = !user.existing_data ? 'new' : 'update';
      if (status !== 'new' && user.actions) {
        for (const action of user.actions) {
          if (action.type === 'deactivate') {
            status = 'deactivate';
          }
          if (action.type === 'activate') {
            status = 'activate';
          }
        }
      }

      return {
        id: user.id,
        integration_user_id: user.integration_user_id,
        fullname: getUserFullName(user.import_data),
        email: user.import_data.email,
        username: user.import_data.username,
        status,
        attributes: !user.errors || !user.errors.length ? mapAllAttributes(user) : null,
        errors: user.errors && user.errors.length ? translateErrors(user.errors) : undefined,
        isSelected: user.execute,
      };
    });
    setPreviewItems({ items: previewUsers, pagination: data.pagination });
  };

  const translateErrors = (errors: string[]): string[][] => {
    return errors.map(err => {
      const [errKeySfx, attrId] = err.split('.');
      const errKey = `x.error.import.${errKeySfx}`;
      let name;
      const attr = props.customAttrs[attrId];
      if (attrId && attr) {
        name = translateContent(attr.name);
      }
      return [errKey, t(errKey, { name })];
    });
  };

  const mapAllAttributes = (userData: ImportPreviewItemI): UserPreviewAttrI[] => {
    const isSysAttr = (attrName: string): attrName is SysAttrs => {
      return attrName in props.sysAttrs;
    };
    const handleValByType = (
      val: string | number | boolean | null,
      customAttr: AttributeI | undefined,
    ): string => {
      if (typeof val === 'boolean') {
        return val ? t('x.company.import.is_admin_yes_value') : t('x.company.import.is_admin_no_value');
      } else if (val) {
        const slug = (customAttr && customAttr.slug) || '';
        if (isSysAttr(slug)) {
          return props.sysAttrs[slug][val];
        }
        return String(val);
      }
      return '';
    };

    return userData.actions.reduce<UserPreviewAttrI[]>(
      (acc, act) => [
        ...acc,
        ...Object.entries(act.import_data).map(([attrKey, attrVal]) => {
          const { new: newAttr, current } = attrVal;
          const customAttr = props.customAttrs[attrKey];
          let label: string[] | undefined =
            attrNames[attrKey] || (customAttr && ['BE', translateContent(customAttr.name)]);
          if (!label) {
            logger.warn(`missing label for attribute ${attrKey}`);
            label = [attrKey, attrKey];
          }
          return {
            label: label,
            value: handleValByType(newAttr, customAttr),
            prevValue: handleValByType(current, customAttr),
          };
        }),
      ],
      [],
    );
  };

  const startImport = async () => {
    setStartingImport(true);
    try {
      await postChangeImportState(importId, { confirmed: true });
      getImportStatus();
      setShowProgress(true);
    } catch (err) {
      logError('Error on starting import', '' + err, 'UsersPreviewList.tsx');
    }
    setStartingImport(false);
  };

  const getImportStatus = async () => {
    importStatusToken.current = Axios.CancelToken.source();
    try {
      const { data } = await fetchImportStatus(
        importId,
        {},
        { cancelToken: importStatusToken.current.token },
      );
      if (data) {
        switch (data.status) {
          case 'executing':
            setImportProgress(data.stats.progress);
            importTimeout.current = window.setTimeout(getImportStatus, 1000);
            break;
          case 'successful':
          case 'failed':
            setShowProgress(false);
            props.history.replace(routes.company.importSummary(importId));
            break;
        }
      }
    } catch (err) {
      logError('Error on getting import status', '' + err, 'UsersPreviewList.tsx');
    }
  };

  const triggerCancelImport = () => {
    cancelImport(importId);
    goToIntegrations({ importCanceled: true });
  };

  const goToIntegrations = (state?: Record<string, any>) => {
    props.history.replace(routes.company.settings, state);
  };

  const updateIncludeItemState = (isChecked: boolean, id: string) => {
    setPreviewItems(
      produce((draft: PreviewItemsI | undefined) => {
        const selectedItemIndex = draft && draft.items.findIndex(item => item.id === id);
        if (selectedItemIndex !== -1 && selectedItemIndex !== undefined && draft) {
          draft.items[selectedItemIndex].isSelected = isChecked;
        }
        return draft;
      }),
    );
  };

  const toggleIncludeUser = async (isChecked: boolean, id: string) => {
    updateIncludeItemState(isChecked, id);
    try {
      await postToggleIncludeUser(id, isChecked);
    } catch (err) {
      logError('Error on toggle execute item state', '' + err, 'UsersPreviewList.tsx');
      updateIncludeItemState(!isChecked, id);
    }
  };

  return (
    <>
      <PageHeader
        title={{ key: 'x.company.import.preview.header_text' }}
        descr={{ key: 'x.company.import.preview.header_description' }}
      />
      <PageContentContainerS>
        {showProgress ? (
          <ProgressBox>
            <ProgressBar t={t} width="40vw" progress={importProgress} progressTxt="Importing" />
          </ProgressBox>
        ) : (
          <ListContainer>
            <InfoSortingBlock $isLoading={loadingPreview}>
              {previewItems && (
                <ItemsAmount
                  pagination={previewItems.pagination}
                  onePageKey="x.users.list.users_amount"
                  multyPageKey="x.users.list.users_amount_from_to"
                />
              )}
            </InfoSortingBlock>
            {/* Postponed: Select all items
            <ToggleSelectAllBox>
              <CheckBox id="toggleSelectAll" label="All" labelKey="All" onChange={toggleIncludeAll} />
            </ToggleSelectAllBox> */}
            {loadingPreview ? (
              <LoadingSpinner marginTop={60} />
            ) : (
              previewItems && (
                <MainContainerAnimation>
                  {previewItems.items.length ? (
                    <ItemsListContainer>
                      {previewItems.items.map(item => (
                        <UserPreviewItem key={item.id} userData={item} onToggleCheckbox={toggleIncludeUser} />
                      ))}
                    </ItemsListContainer>
                  ) : (
                    <NoItemsText>{t('x.company.import.preview.no_items')}</NoItemsText>
                  )}
                </MainContainerAnimation>
              )
            )}
            {previewItems && (
              <>
                <Pagination
                  t={t}
                  totalItems={previewItems.pagination.total}
                  pageSize={previewItems.pagination.per_page}
                  onChange={getUsers}
                  marginTop={60}
                  history={props.history}
                  location={props.location}
                  defaultCurrPage={initialPage}
                />
                <ImportBtnsBox>
                  <SecondaryButton
                    data-translation="x.company.import.preview.cancel_import_button"
                    disabled={startingImport}
                    onClick={triggerCancelImport}
                    $margin="0 15px 0 0"
                  >
                    {t('x.company.import.preview.cancel_import_button')}
                  </SecondaryButton>
                  {!!previewItems.items.length && (
                    <PrimaryButtonLoading
                      data-translation="x.company.import.preview.start_import_button"
                      isLoading={startingImport}
                      onClick={startImport}
                      disabled={isSaveDisabled}
                    >
                      {t('x.company.import.preview.start_import_button')}
                    </PrimaryButtonLoading>
                  )}
                </ImportBtnsBox>
              </>
            )}
          </ListContainer>
        )}
      </PageContentContainerS>
    </>
  );
};
const mapState2Props = ({ user, attributes }: StoreI): StateProps => ({
  sysAttrs: {
    lls_content_language: user.session.contentLocales,
    lls_system_language: user.session.systemLocales,
    lls_timezone: user.session.timezones,
  },
  customAttrs: attributes.byId,
  loadedLocalesAndTimezones: user.session.loadedLoacalesAndTimezones,
});

export default connect(mapState2Props)(UsersPreviewList);
